import { createLookupMap } from '@common/utils';
import { useMemo } from 'react';

/**
 * A React hook that memoizes a lookup Map created from an array of items.
 * The map is re-created only when the 'items' array or the 'keyFn' reference changes.
 * This provides efficient O(1) lookup performance after the initial O(N) map creation.
 * @param items The array of items to create a memoized map for.
 * @param keyFn A function that returns the key for each item.
 * @returns A memoized Map for efficient item lookup.
 */
export function useLookupMap<T, K extends keyof any>(
  items: T[],
  keyFn: (item: T) => K,
): Map<K, T> {
  // useMemo ensures that the map is only re-created if 'items' or 'keyFn' changes.
  return useMemo(() => createLookupMap(items, keyFn), [items, keyFn]);
}
